import { bind, classNames, Component, computed, effect, h, signal, tag } from "omi";
import every from "lodash-es/every";
import some from "lodash-es/some";

import { NWCarouselArrow } from "@/wc/components/base/nw-carousel-arrow";
import {
  EditedEvent,
  FilledEvent,
  fireEvent,
  HydratedEvent,
  InReviewFocusedEvent,
  ValidationEvents,
} from "../events.ts";
import type { ValidationBase } from "../validationBase.tsx";
import { AuthEvents } from "@/wc/components/nw-auth-manager";
import { getElementsFromSlot } from "@/utils/elements.ts";
import { networkSignal } from "@/utils/signals";
import { ValidationStatus } from "@/api";
import { NWBadge } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { BoxStatus, ControlStatus } from "../models.ts";
import { NWValidationControls } from "../nw-validation-controls";
import style from "./style.css?inline";

export type Validation = {
  id: string;
  filled: boolean;
  element: ValidationBase;
};

export type ValidationBoxProps = {
  id: string;
  status: BoxStatus;
};

@tag("nw-validation-box")
export class NWValidationBox extends Component<ValidationBoxProps> {
  static css = [tailwind, style];

  static propTypes = {
    id: String,
  };

  private validationsAsMap = new Map<Validation["id"], Validation>();
  private validations = signal<Validation[]>([]);
  private currentValidation = signal<Validation | undefined>(undefined);
  private isDoneEnabled = computed(() => every(this.validations.value, "filled"));
  controlStatus = signal<ControlStatus>(ControlStatus.INCOMPLETE);
  boxStatus = signal<BoxStatus>(BoxStatus.INCOMPLETE);
  private minHeight = signal(0);
  private doneSignal = networkSignal<ValidationStatus[]>({
    retry: { delayMs: 1000, maxAttempts: 3, backoffMultiplier: 2 },
  });
  private reviewSignal = networkSignal({ retry: { delayMs: 1000, maxAttempts: 3, backoffMultiplier: 2 } });
  private statusChangeSignal = networkSignal({ retry: { delayMs: 1000, maxAttempts: 3, backoffMultiplier: 2 } });

  installed() {
    const elements = getElementsFromSlot<ValidationBase>(this.shadowRoot, "validations-slot", ["nw-validation-*"]);
    const validations = this.transformToValidation(elements);
    validations.forEach(v => this.validationsAsMap.set(v.id, v));

    this.minHeight.value = this.calculateMinHeight(elements);

    this.validations.value = validations;
    this.currentValidation.value = validations[0];

    effect(() => this.renderCurrentValidation(elements));

    addEventListener(ValidationEvents.FILLED, this.handleFilledEvent as EventListener);
    addEventListener(ValidationEvents.HYDRATED, this.handleHydration as EventListener);
    addEventListener(
      ValidationEvents.EDITED,
      async event => await this.handleStatusChange(event as CustomEvent<EditedEvent>),
    );
    addEventListener(
      ValidationEvents.IN_REVIEW_FOCUSED,
      async event => await this.handleStatusChange(event as CustomEvent<InReviewFocusedEvent>),
    );
  }

  @bind
  private handleHydration(event: CustomEvent<HydratedEvent>) {
    const { id } = event.detail;
    // Ignore the hydration event if the validation is not part of the current box.
    if (!this.validationsAsMap.has(id)) {
      return;
    }

    this.calculateAndSetControlStatus(false);
  }

  @bind
  private handleFilledEvent(event: CustomEvent<FilledEvent>) {
    event.stopPropagation();

    const { id, isFilled } = event.detail;

    const validations = [...this.validations.value];
    const target = validations.find(v => v.id === id);
    if (target == null) {
      return;
    }

    target.filled = isFilled;
    this.validations.value = validations;
  }

  @bind
  private async handleStatusChange(event: CustomEvent<EditedEvent> | CustomEvent<InReviewFocusedEvent>) {
    event.stopPropagation();

    const { id } = event.detail;
    const validation = this.validationsAsMap.get(id);

    // Not part of the current box
    if (validation == null) {
      return;
    }

    const promises = this.validations.value.map(
      async v => await v.element.setStatusAndSave(ValidationStatus.Incomplete, true),
    );

    await this.statusChangeSignal.run(async () => await Promise.all(promises));

    this.calculateAndSetControlStatus(true);
  }

  @bind
  private calculateAndSetControlStatus(triggerEvent: boolean = true) {
    const controlStatus = this.getControlStatus(
      this.validations.value.map(v => v.element.status?.value || ControlStatus.INCOMPLETE),
    );

    return this.setControlStatus(controlStatus, triggerEvent);
  }

  @bind
  async handleDone(validations: Validation[]) {
    if (!Store.user.isLoggedIn.value) {
      const loginEvent = new Event(AuthEvents.LoginClicked);
      dispatchEvent(loginEvent);
      return;
    }
    const hasOptionalValidations = validations.some(v => v.element.isOptional());

    if (hasOptionalValidations) {
      fireEvent(ValidationEvents.OPTIONAL_FOCUSED, { id: this.id });
    }

    await this.processValidations(validations);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackValidationBoxActionClicked(Store.project.id.value!, this.id, "done");
    });
  }

  @bind
  private async processValidations(validations: Validation[]) {
    try {
      const promisesToProcess = validations.map(v => v.element.process());
      await this.doneSignal.run(async () => {
        const results = await Promise.all(promisesToProcess);
        this.setControlStatus(this.getControlStatus(results));
        return results;
      });
    } catch (error) {
      this.setControlStatus(ControlStatus.ERROR);
    }
  }

  @bind
  async handleMarkForReview(validations: Validation[]) {
    if (!Store.user.isLoggedIn.value) {
      const loginEvent = new Event(AuthEvents.LoginClicked);
      dispatchEvent(loginEvent);
      return;
    }

    await this.reviewSignal.run(async () => await this.processMarkForReview(validations));

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackValidationBoxActionClicked(Store.project.id.value!, this.id, "mark for review");
    });
  }

  @bind
  private async processMarkForReview(validations: Validation[]) {
    try {
      const promisesToProcess = validations.map(v => v.element.setStatusAndSave(ValidationStatus.InReview));
      await Promise.all(promisesToProcess);
      this.setControlStatus(ControlStatus.REVIEW);
    } catch (error) {
      this.setControlStatus(ControlStatus.ERROR);
    }
  }

  getControlStatus(results: ValidationStatus[]): ControlStatus {
    if (some(results, res => res === ValidationStatus.InReview)) {
      return ControlStatus.REVIEW;
    }

    for (const res of results) {
      if (res === ValidationStatus.Incorrect) {
        return ControlStatus.INCORRECT;
      }

      if (res === ValidationStatus.Incomplete) {
        return ControlStatus.INCOMPLETE;
      }
    }

    return ControlStatus.COMPLETE;
  }

  @bind
  private setControlStatus(value: ControlStatus, triggerEvent: boolean = true) {
    if (this.controlStatus.value === value) {
      return;
    }

    this.controlStatus.value = value;
    const status = this.computeBoxStatus(value);
    this.boxStatus.value = status;

    if (triggerEvent) {
      this.triggerBoxStatusChangedEvent(status);
    }

    return status;
  }

  @bind
  computeBoxStatus(controlStatus: ControlStatus): BoxStatus {
    switch (controlStatus) {
      case ControlStatus.COMPLETE:
        return BoxStatus.COMPLETE;
      case ControlStatus.REVIEW:
        return BoxStatus.REVIEW;
      default:
        return BoxStatus.INCOMPLETE;
    }
  }

  @bind
  public getBoxStatus() {
    return this.boxStatus.value;
  }

  @bind
  triggerBoxStatusChangedEvent(boxStatus: BoxStatus | undefined) {
    if (boxStatus == null) {
      return;
    }

    this.fire("validation-box-status-changed", { boxStatus }, { bubbles: true, composed: true });
  }

  @bind
  private renderCurrentValidation(validationElements: ValidationBase[]) {
    validationElements.forEach(validation => {
      if (validation.id === this.currentValidation.value?.id) {
        validation.classList.remove("hidden");
      } else {
        validation.classList.add("hidden");
      }
    });
  }

  @bind
  private navigate(direction: "forward" | "back"): void {
    const currentValidation = this.currentValidation.peek();
    if (currentValidation?.id == null) {
      return;
    }

    let nextId;
    const currentIndex = this.validations.peek().findIndex(v => v.id === currentValidation.id);

    if (direction === "forward") {
      nextId = Math.min(currentIndex + 1, this.validations.value.length - 1);
    } else {
      nextId = Math.max(currentIndex - 1, 0);
    }

    this.currentValidation.value = this.validations.value[nextId];
  }

  private calculateMinHeight(elements: ValidationBase[]) {
    const minHeight = elements.reduce((acc, e) => {
      if (acc < e.offsetHeight) {
        acc = e.offsetHeight;
      }
      return acc;
    }, 0);
    return minHeight + 4;
  }

  private getIndex(validation: Validation | undefined): number {
    return validation ? this.validations.peek().findIndex(v => v.id === validation.id) : -1;
  }

  private transformToValidation(elements: ValidationBase[]): Validation[] {
    return elements.map<Validation>(element => {
      return { id: element.id, filled: false, element };
    });
  }

  render() {
    const totalValidations = this.validations.value.length;
    const currentValidation = this.currentValidation.value;
    const currentIndex = this.getIndex(currentValidation);
    const pageDisplay = `${currentIndex + 1}/${totalValidations}`;

    const isMultiValidationBox = totalValidations > 1 && !!currentValidation;
    const canGoBack = isMultiValidationBox && currentIndex > 0;
    const canGoForward = isMultiValidationBox && currentIndex < totalValidations - 1;

    return (
      <div class="flex box-border flex-col space-y-6 my-16 justify-center">
        <div
          class={classNames(
            "rounded-3xl py-10 px-2 flex transition-colors",
            { "bg-primary border-2 border-gray-50": this.boxStatus.value === BoxStatus.INCOMPLETE },
            { "bg-success-50 border-2 border-success-300": this.boxStatus.value === BoxStatus.REVIEW },
            { "bg-success-100 border-2 border-success-50": this.boxStatus.value === BoxStatus.COMPLETE },
          )}
        >
          <div
            style={{ minHeight: `${Math.ceil(this.minHeight.value)}px`, height: "100%" }}
            class={classNames("flex grow self-stretch", {
              "mx-4 md:mx-6 lg:mx-8": !isMultiValidationBox,
            })}
          >
            <div class="flex items-center mx-6 text-quaternary">
              {isMultiValidationBox && (
                <NWCarouselArrow
                  status={this.boxStatus.value}
                  class={classNames({ invisible: !canGoBack })}
                  direction="left"
                  onClick={() => this.navigate("back")}
                />
              )}
            </div>

            <slot id="validations-slot" class="flex items-start flex-1"></slot>

            <div class="flex items-center mx-6 relative text-quaternary">
              {isMultiValidationBox && (
                <>
                  <div class="absolute top-0 left-0">
                    <NWBadge withIndicator={false}>{pageDisplay}</NWBadge>
                  </div>

                  <NWCarouselArrow
                    status={this.boxStatus.value}
                    class={classNames({ invisible: !canGoForward })}
                    direction="right"
                    onClick={() => this.navigate("forward")}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <NWValidationControls
          status={this.controlStatus.value}
          isDoneEnabled={this.isDoneEnabled.value}
          onDone={() => this.handleDone(this.validations.value)}
          onMarkForReview={() => this.handleMarkForReview(this.validations.value)}
          isLoadingDone={this.doneSignal.signal.isLoading.value}
          isLoadingReview={this.reviewSignal.signal.isLoading.value}
        />
      </div>
    );
  }
}
